import React, { useState, useEffect, useRef, useCallback } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { IoMdArrowBack } from "react-icons/io";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../../src/Config.js";
import Pagination from "../../../src/Component/Pagination";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

function HRMListView({ isSidebarExpanded }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const listType = searchParams.get("list_type") || "required_positions";
  const projectId = searchParams.get("project_id") || "";
  const filterOption = searchParams.get("filter_option") || "all";
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";
  const statusFilter = searchParams.get("status") || "";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [projectName, setProjectName] = useState("");

  const containerRef = useRef(null);

  // const formatDate = (date) => {
  //   if (!date) return "NA";
  //   try {
  //     const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  //     return new Date(date)
  //       .toLocaleDateString("en-CA", options)
  //       .replace(/-/g, "/");
  //   } catch (error) {
  //     return "NA";
  //   }
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";

    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "Invalid date";

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}/${get("month")}/${get("year")}`;
  };

  const fetchData = async (
    currentPage,
    currentItemsPerPage,
    searchQuery = "",
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      if (!authKey) {
        console.error("No authentication key found");
        setLoading(false);
        return;
      }

      const params = {
        list_type: listType,
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (projectId && projectId.trim() !== "") {
        params.project_id = projectId;
      }

      if (statusFilter && statusFilter.trim() !== "") {
        params.status = statusFilter;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.query = searchQuery.trim();
      }

      console.log("Fetching HRM list with params:", params);

      const response = await axios.get(
        `${API_URL}/api/kpi/human-resource-management/list`,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
          params,
        },
      );

      console.log("API Response:", response.data);

      setReports(response.data.items || []);
      setTotalItems(response.data.total_items || 0);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching HRM list:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      setLoading(false);
      setReports([]);
      setTotalItems(0);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      setPage(1);
      fetchData(1, itemsPerPage, searchQuery);
    }, 300),
    [
      itemsPerPage,
      listType,
      projectId,
      filterOption,
      startDate,
      endDate,
      statusFilter,
    ],
  );

  useEffect(() => {
    fetchData(page, itemsPerPage, searchInput);
  }, [
    page,
    itemsPerPage,
    listType,
    projectId,
    filterOption,
    startDate,
    endDate,
    statusFilter,
  ]);

  const handlePaginationChange = (currentPage, newItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(newItemsPerPage);
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (value.trim() === "") {
      setPage(1);
      fetchData(1, itemsPerPage, "");
    } else {
      debouncedSearch(value);
    }
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const closePopups = () => {
    setIsSortPopup(false);
  };

  const handleRefresh = () => {
    setSearchInput("");
    setPage(1);
    fetchData(1, itemsPerPage, "");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "present at site":
        return "#2E8B57";
      case "appointment issued (yet to join)":
        return "#2980B9";
      case "active":
        return "#2980B9";
      case "inactive":
        return "#6b7280";
      case "on leave":
        return "#D4AC0D";
      case "resigned":
        return "#F81700";
      default:
        return "black";
    }
  };

  const getListTypeDisplay = () => {
    switch (listType) {
      case "required_positions":
        return "Total Workforce Required";
      case "available_manpower":
        return "Available Manpower";
      case "pending_positions":
        return "Balance Position";
      default:
        return "Human Resource";
    }
  };

  const formatDates = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const formatter = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      return formatter.format(date);
    } catch (error) {
      return "";
    }
  };

  // Render table columns based on list type
  const renderTableHeaders = () => {
    if (listType === "required_positions") {
      return (
        <tr>
          <th className="center-alignment">SR. NO.</th>
          <th>PROJECT</th>
          <th>POSITION</th>
          <th className="center-alignment">STATUS</th>
          <th className="center-alignment">REQUIRED POSITIONS</th>
          <th className="center-alignment">FILLED POSITIONS</th>
          <th className="center-alignment">VACANT POSITIONS</th>
          <th>EXPECTED PROFILE</th>
        </tr>
      );
    } else if (listType === "available_manpower") {
      return (
        <tr>
          <th className="center-alignment">SR. NO.</th>
          <th>PROJECT</th>
          <th>PERSON NAME</th>
          <th>EC NO</th>
          <th>CONTACT NO</th>
          <th>POSITION</th>
          <th>EMAIL</th>
          <th className="center-alignment">STATUS</th>
          <th>JOINING DATE</th>
          <th>EXPECTED LEAVING DATE</th>
        </tr>
      );
    } else if (listType === "pending_positions") {
      return (
        <tr>
          <th className="center-alignment">SR. NO.</th>
          <th>PROJECT</th>
          <th>POSITION</th>
          <th className="center-alignment">STATUS</th>
          <th className="center-alignment">REQUIRED POSITIONS</th>
          <th className="center-alignment">FILLED POSITIONS</th>
          <th className="center-alignment">VACANT POSITIONS</th>
          <th>EXPECTED PROFILE</th>
        </tr>
      );
    }
  };

  const renderTableRow = (report, index) => {
    if (listType === "required_positions") {
      return (
        <tr key={report.id} className="hoverable-row">
          <td className="center-alignment">
            {(page - 1) * itemsPerPage + index + 1}
          </td>
          <td>{report.project_name || "NA"}</td>
          <td>{report.position || "NA"}</td>
          <td className="status-schedules">
            <div
              style={{
                backgroundColor: getStatusColor(report.requirement_status),
                width: "6vw",
              }}
            >
              {report.requirement_status
                ? report.requirement_status.charAt(0).toUpperCase() +
                  report.requirement_status.slice(1).toLowerCase()
                : "NA"}
            </div>
          </td>
          <td className="center-alignment">{report.no_of_positions || 0}</td>
          <td className="center-alignment">{report.filled_positions || 0}</td>
          <td className="center-alignment">{report.vacant_positions || 0}</td>
          <td>{report.expected_profile || "NA"}</td>
        </tr>
      );
    } else if (listType === "available_manpower") {
      return (
        <tr key={report.id} className="hoverable-row">
          <td className="center-alignment">
            {(page - 1) * itemsPerPage + index + 1}
          </td>
          <td>{report.project_name || "NA"}</td>
          <td>{report.person_name || "NA"}</td>
          <td>{report.ec_no || "NA"}</td>
          <td>{report.contact_no || "NA"}</td>
          <td>{report.position || "NA"}</td>
          <td>{report.email || "NA"}</td>
          <td className="status-schedules">
            <div
              style={{
                backgroundColor: getStatusColor(report.status),
                width: "10vw",
              }}
            >
              {report.status
                ? report.status.charAt(0).toUpperCase() +
                  report.status.slice(1).toLowerCase()
                : "NA"}
            </div>
          </td>
          <td>{formatDate(report.joining_expected_date)}</td>
          <td>{formatDate(report.excepted_date_leaving)}</td>
        </tr>
      );
    } else if (listType === "pending_positions") {
      return (
        <tr key={report.id} className="hoverable-row">
          <td className="center-alignment">
            {(page - 1) * itemsPerPage + index + 1}
          </td>
          <td>{report.project_name || "NA"}</td>
          <td>{report.position || "NA"}</td>
          <td className="status-schedules">
            <div
              style={{
                backgroundColor: getStatusColor(report.requirement_status),
                width: "6vw",
              }}
            >
              {report.requirement_status
                ? report.requirement_status.charAt(0).toUpperCase() +
                  report.requirement_status.slice(1).toLowerCase()
                : "NA"}
            </div>
          </td>
          <td className="center-alignment">{report.no_of_positions || 0}</td>
          <td className="center-alignment">{report.filled_positions || 0}</td>
          <td className="center-alignment">{report.vacant_positions || 0}</td>
          <td>{report.expected_profile || "NA"}</td>
        </tr>
      );
    }
  };

  return (
    <div
      className={`data-list-content-container page-content ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="data-list-content" ref={containerRef}>
        <div className="data-list-header">
          <div className="data-list-header-content">
            <div className="data-list-heading-content-left-side">
              {/* <Tooltip title="Back" arrow>
                <IoMdArrowBack className="back-company" onClick={handleBack} />
              </Tooltip> */}
              <span className="report-name">
                Human Resource Management - {getListTypeDisplay()}
              </span>

              {projectName && (
                <span
                  className="department-show"
                  style={{
                    color: "green",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {projectName}
                </span>
              )}

              {startDate && endDate && (
                <span
                  className="option-show"
                  style={{
                    color: "#ce9139ed",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {formatDates(startDate)} - {formatDates(endDate)}
                </span>
              )}
            </div>

            <div className="data-list-heading-content-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="search-bar"
                />
                <IoSearchOutline className="search-bar-icon" />
              </div>

              <Tooltip title="Refresh List" arrow>
                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh className="refresh-icon" />
                </button>
              </Tooltip>

              <button className="sort-button" onClick={handleSortpopup}>
                <PiDotsThreeOutlineVerticalFill className="sort-icon" />
              </button>

              {isSortPopup && (
                <div className="sort-popup-container">
                  <div className="sort-popup-content">
                    <div className="sort-by">SORT BY</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body" onClick={closePopups}>
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : reports.length > 0 ? (
                <table className="table multilevel-data">
                  <thead>{renderTableHeaders()}</thead>
                  <tbody className="data-list-scroll-bodys">
                    {reports.map((report, index) =>
                      renderTableRow(report, index),
                    )}
                  </tbody>
                </table>
              ) : (
                <div className="no-users-container">
                  <div className="no-users-message">
                    No {getListTypeDisplay()} Records Found
                  </div>
                </div>
              )}
            </div>

            {totalItems > 0 && (
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HRMListView;
